/**
 * SignalIQ — Company & Period Context
 * Global year system: selectedYear + comparisonYear persist across all pages.
 * Portfolio → BSP → FHI → Benchmarks all use the same year context.
 * Matches periodHelper.buildPeriodContext interface exactly.
 */
import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  useCallback,
  useEffect,
} from "react";
import { buildPeriodContext } from "../utils/periodHelper";

const CompanyContext = createContext(null);

const STORAGE_KEY = "signaliq_company_context_v1";

function safeNumber(value) {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function loadSavedCompanyContext() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};

    const parsed = JSON.parse(raw);

    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

export function CompanyProvider({ children }) {
  const saved = useMemo(() => loadSavedCompanyContext(), []);

  const [selectedCompanyId, setSelectedCompanyId] = useState(
    safeNumber(saved.selectedCompanyId)
  );

  const [selectedCompany, setSelectedCompany] = useState(
    saved.selectedCompany && typeof saved.selectedCompany === "object"
      ? saved.selectedCompany
      : null
  );

  const [selectedYear, setSelectedYear] = useState(
    safeNumber(saved.selectedYear)
  );

  const [comparisonYear, setComparisonYear] = useState(
    safeNumber(saved.comparisonYear)
  );

  const [compareMode, setCompareMode] = useState(
    Boolean(saved.compareMode && safeNumber(saved.comparisonYear))
  );

  useEffect(() => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          selectedCompanyId,
          selectedCompany,
          selectedYear,
          comparisonYear,
          compareMode,
        })
      );
    } catch {
      // Ignore localStorage failures.
    }
  }, [
    selectedCompanyId,
    selectedCompany,
    selectedYear,
    comparisonYear,
    compareMode,
  ]);

  const selectCompany = useCallback((company) => {
    if (typeof company === "number" || typeof company === "string") {
      const id = safeNumber(company);
      setSelectedCompanyId(id);
      setSelectedCompany(null);
      return;
    }

    if (company && company.id) {
      setSelectedCompanyId(safeNumber(company.id));
      setSelectedCompany(company);
      return;
    }

    setSelectedCompanyId(null);
    setSelectedCompany(null);
    setSelectedYear(null);
    setComparisonYear(null);
    setCompareMode(false);
  }, []);

  const selectYear = useCallback((year) => {
    setSelectedYear(safeNumber(year));
  }, []);

  const selectComparisonYear = useCallback((year) => {
    const y = safeNumber(year);
    setComparisonYear(y);
    setCompareMode(y !== null);
  }, []);

  const toggleCompare = useCallback(() => {
    setCompareMode((prev) => {
      if (prev) {
        setComparisonYear(null);
        return false;
      }

      return true;
    });
  }, []);

  const clearCompanyContext = useCallback(() => {
    setSelectedCompanyId(null);
    setSelectedCompany(null);
    setSelectedYear(null);
    setComparisonYear(null);
    setCompareMode(false);

    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // Ignore localStorage failures.
    }
  }, []);

  const periodContext = useMemo(() => {
    if (!selectedCompany && !selectedYear) {
      return buildPeriodContext(null, null);
    }

    return buildPeriodContext({
      company: selectedCompany,
      selectedYear,
      comparisonYear: compareMode ? comparisonYear : null,
      mode: compareMode ? "compare" : "single",
    });
  }, [selectedCompany, selectedYear, comparisonYear, compareMode]);

  const periodLabel = useMemo(() => {
    if (compareMode && comparisonYear && periodContext.active) {
      return `FY${periodContext.active} vs FY${comparisonYear}`;
    }

    if (periodContext.active) {
      return `FY${periodContext.active}`;
    }

    return "";
  }, [periodContext.active, compareMode, comparisonYear]);

  const value = useMemo(
    () => ({
      selectedCompanyId,
      selectedCompany,
      selectCompany,

      selectedYear,
      selectYear,

      comparisonYear,
      selectComparisonYear,

      compareMode,
      toggleCompare,

      clearCompanyContext,

      periodContext,
      periodLabel,
    }),
    [
      selectedCompanyId,
      selectedCompany,
      selectCompany,
      selectedYear,
      selectYear,
      comparisonYear,
      selectComparisonYear,
      compareMode,
      toggleCompare,
      clearCompanyContext,
      periodContext,
      periodLabel,
    ]
  );

  return (
    <CompanyContext.Provider value={value}>
      {children}
    </CompanyContext.Provider>
  );
}

export const useCompany = () => useContext(CompanyContext);